import { useEffect, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useProfile } from '@/api/profile'
import { api } from '@/lib/api'
import { runStaggered } from '@/lib/idleTask'

const TWO_MIN = 2 * 60_000
const FIVE_MIN = 5 * 60_000

/**
 * Prefetch des queries spécifiques à l'espace Manager.
 *
 * Démarre quand `profile.company` est dispo et qu'on est sûr d'être manager
 * (le routing Manager.tsx redirige les agents vers /dashboard avant qu'on
 * arrive ici, donc on n'y entre QUE si role=MANAGER/ADMIN).
 *
 * Toutes les queries reprennent EXACTEMENT les queryKey + queryFn des hooks
 * existants — sinon TanStack ne reconnaît pas le cache et refetch quand
 * l'user navigue. Si un hook source change son URL, faut le synchroniser ici.
 */
export function ManagerBootProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const { data: profile } = useProfile()
  const companyId = profile?.company
  // A supervisor only opens live calls, analytics and history.
  const superviseur = profile?.access?.role === 'supervisor'

  useEffect(() => {
    if (!companyId || superviseur) return

    // Cascade : 5 prefetch en lots de 2 toutes les 300ms, après 2s d'attente.
    // Étale la charge sur le worker uvicorn unique : ~2 req parallèles à la
    // fois au lieu de 5 simultanées. Toute la cascade prend ~3.5s, l'user
    // ne sent rien (idle background) et le backend ne sature pas.
    return runStaggered([
      // Company détail — utilisé partout (CompanyView, badges Pro)
      () => {
        queryClient.prefetchQuery({
          queryKey: ['company', companyId],
          queryFn: async () => {
            const { data } = await api.get(`/api/company/${companyId}/`)
            return data
          },
          staleTime: FIVE_MIN,
        })
      },
      // AI agents — tab AI Agents
      () => {
        queryClient.prefetchQuery({
          queryKey: ['ai-agents', companyId],
          queryFn: async () => {
            const { data } = await api.get(
              `/api/companies/${companyId}/ai-agents/`,
            )
            return data
          },
          staleTime: TWO_MIN,
        })
      },
      // Phone numbers — tab Numéros
      () => {
        queryClient.prefetchQuery({
          queryKey: ['company-phone-numbers', companyId],
          queryFn: async () => {
            const { data } = await api.get(
              `/api/companies/${companyId}/phone-numbers/`,
            )
            return data
          },
          staleTime: TWO_MIN,
        })
      },
      // Team members — tab Équipe. Le hook source normalise array | {results:[]}.
      () => {
        queryClient.prefetchQuery({
          queryKey: ['team-members'],
          queryFn: async () => {
            const { data } = await api.get<unknown>('/api/team/')
            if (Array.isArray(data)) return data
            return (data as { results?: unknown[] }).results || []
          },
          staleTime: TWO_MIN,
        })
      },
      // IVR flows — tab Flux IVR
      () => {
        queryClient.prefetchQuery({
          queryKey: ['ivr-flows'],
          queryFn: async () => {
            const { data } = await api.get('/api/ivr-flows/')
            return data
          },
          staleTime: TWO_MIN,
        })
      },
      // Note : `connected-calendars` retiré — logique trop complexe à
      // dupliquer (merge Google + Microsoft). useCalendars fera son fetch
      // normal quand l'user clique sur le tab Calendriers.
    ])
  }, [companyId, superviseur, queryClient])

  return <>{children}</>
}
